import type { ReportStrategyPolicyVersion, ReportStrategyResponse } from "../types/reports";
import type {
  ComposeOutputMode,
  StrategyActivePineSummary,
  ExitHookReadiness,
  InjectReadiness,
  PineParseStatus,
  PolicyCombineErrorCode,
  ReportStrategyPineFields,
  ReportStrategyPineParseResult,
  ReportStrategyPineRequirementsResponse,
} from "../types/reportStrategyPine";
import { isPolicyTreeBundleV1, isPolicyTreeV2 } from "../types/tradePolicy";
import {
  getComposeModeHelpEntry,
  getEffectiveInjectReadiness,
  getEffectiveParseStatus,
  getExitHookReadinessDisplay,
  getInjectReadinessDisplay,
  getParseStatusDisplay,
  getPineScriptKindDisplay,
  isIndicatorPineScript,
  shouldShowInjectReadinessBadge,
} from "./pineIrUi";
import { mergeStrategyWithParseSummary, resolveDefaultComposeMode } from "./strategyComposeUi";

export function formatPineParseStatusLabel(
  status?: PineParseStatus | string | null,
  apiMap?: Record<string, string | { label?: string; description?: string }> | null
): string {
  return getParseStatusDisplay(status, apiMap)?.shortLabel ?? (status ? String(status) : "—");
}

export function pineParseStatusColorScheme(status?: PineParseStatus | string | null): string {
  switch (status) {
    case "full":
      return "green";
    case "partial":
      return "yellow";
    case "failed":
      return "red";
    default:
      return "gray";
  }
}

export function formatInjectReadinessLabel(
  readiness?: InjectReadiness | string | null,
  apiMap?: Record<string, string | { label?: string; description?: string }> | null
): string {
  if (!shouldShowInjectReadinessBadge(readiness)) return "—";
  return getInjectReadinessDisplay(readiness, apiMap)?.shortLabel ?? (readiness ? String(readiness) : "—");
}

export function injectReadinessColorScheme(readiness?: InjectReadiness | string | null): string {
  switch (readiness) {
    case "ready":
      return "green";
    case "needs_review":
      return "yellow";
    case "unsupported":
      return "red";
    case "not_applicable":
      return "gray";
    default:
      return "gray";
  }
}

export function formatExitHookReadinessLabel(
  readiness?: ExitHookReadiness | string | null,
  apiMap?: Record<string, string | { label?: string; description?: string }> | null
): string {
  return getExitHookReadinessDisplay(readiness, apiMap)?.shortLabel ?? (readiness ? String(readiness) : "—");
}

export function formatPineScriptKindLabel(kind?: string | null): string {
  return getPineScriptKindDisplay(kind)?.shortLabel ?? (kind ? String(kind) : "—");
}

/** Known BE `compose_injection_previews.injection_site` values → short chip label */
const COMPOSE_INJECTION_SITE_LABELS: Record<string, string> = {
  entry_calls: "Entry calls",
  separate_generated_file: "Separate file",
};

export function formatInjectionSiteLabel(
  site?: string | null,
  apiMap?: Record<string, string | { label?: string; description?: string }> | null
): string | null {
  if (!site?.trim()) return null;
  const key = site.trim();
  const fromApi = apiMap?.[key];
  if (typeof fromApi === "string" && fromApi.trim()) return fromApi.trim();
  if (fromApi && typeof fromApi === "object" && !Array.isArray(fromApi)) {
    const label = typeof fromApi.label === "string" ? fromApi.label.trim() : "";
    if (label) return label;
  }
  return COMPOSE_INJECTION_SITE_LABELS[key] ?? null;
}

export function exitHookReadinessColorScheme(readiness?: ExitHookReadiness | string | null): string {
  switch (readiness) {
    case "ready":
      return "green";
    case "partial":
      return "yellow";
    case "unsupported":
      return "red";
    case "not_applicable":
      return "gray";
    default:
      return "gray";
  }
}

export function canMergePolicyIntoStrategy(
  fields: ReportStrategyPineFields | null | undefined,
  parsed?: ReportStrategyPineParseResult | null
): boolean {
  const status = getEffectiveParseStatus(fields, parsed);
  const inject = getEffectiveInjectReadiness(fields, parsed);
  const hasPineContext = Boolean(
    parsed || fields?.pine_source || status || fields?.pine_parse_status
  );
  if (!hasPineContext) return false;
  if (status === "failed") return false;
  if (inject === "unsupported" || inject === "not_applicable") return false;
  return inject === "ready" || inject === "needs_review";
}

export function mergeDisabledReason(
  fields: ReportStrategyPineFields | null | undefined,
  parsed?: ReportStrategyPineParseResult | null
): string | null {
  const status = getEffectiveParseStatus(fields, parsed);
  const inject = getEffectiveInjectReadiness(fields, parsed);
  const hasPineContext = Boolean(
    parsed || fields?.pine_source || status || fields?.pine_parse_status
  );
  if (!hasPineContext) {
    return "Add and validate Pinescript source before merging policy.";
  }
  if (status === "failed") {
    return "Fix Pinescript parse errors before merging policy into the strategy.";
  }
  if (inject === "not_applicable") {
    return "Merge is only for strategy scripts. Use separate indicator mode.";
  }
  if (inject === "unsupported") {
    return "This strategy script cannot accept merged policy filters. Fix entries or add // @tessera:inject markers.";
  }
  return null;
}

export function strategyHasPineSignals(fields: ReportStrategyPineFields | null | undefined): boolean {
  return Boolean(
    fields?.pine_parse_status ||
      fields?.inject_readiness ||
      fields?.exit_hook_readiness ||
      fields?.pine_source ||
      fields?.has_composed_pine ||
      fields?.has_indicator_pine
  );
}

const COMBINE_ERROR_FALLBACK: Record<string, string> = {
  unsatisfiable_keep_and: "Two keep-only filters disagree on the same path.",
  conflicting_mgmt_override: "Same condition has different stop/take-profit override parameters.",
  exceeds_node_limit: "Combined policy tree is too large.",
};

export function formatCombinePolicyError(
  code: PolicyCombineErrorCode | undefined,
  message?: string,
  requirements?: ReportStrategyPineRequirementsResponse | null
): string {
  if (message?.trim()) return message.trim();
  if (code && requirements?.combine_error_codes?.[code]?.message) {
    return requirements.combine_error_codes[code].message!;
  }
  if (code && COMBINE_ERROR_FALLBACK[code]) return COMBINE_ERROR_FALLBACK[code];
  return code ? String(code) : "Combine failed.";
}

/**
 * Segment count for combine guardrails. Returns `null` when the list row omits
 * `policy_tree` but schema hints a policy exists — defer validation to BE.
 */
export function countPolicyVersionSegments(version: ReportStrategyPolicyVersion): number | null {
  const tree = version.policy_tree;
  if (tree == null) {
    const schema = version.policy_schema_version;
    if (
      schema === "policy_tree.v2" ||
      schema === "policy_tree.v1" ||
      schema === "policy_tree.bundle.v1"
    ) {
      return null;
    }
    return 0;
  }
  if (typeof tree !== "object" || Array.isArray(tree)) return 0;

  if (isPolicyTreeBundleV1(tree)) return tree.segments.length;
  if (isPolicyTreeV2(tree)) return tree.rules?.length ?? 0;

  const record = tree as Record<string, unknown>;
  if (Array.isArray(record.segments)) return record.segments.length;
  if (record.segment && typeof record.segment === "object") return 1;
  if (record.root && typeof record.root === "object") return 1;
  if (record.action && record.root) return 1;
  if (record.kind === "group" || record.kind === "rule") return 1;

  return 0;
}

export function isPolicyBundleVersion(v: ReportStrategyPolicyVersion): boolean {
  if (v.policy_schema_version === "policy_tree.bundle.v1") return true;
  const tree = v.policy_tree as { schema_version?: string } | null | undefined;
  return tree?.schema_version === "policy_tree.bundle.v1";
}

function isPolicySchemaVersionLabel(label: string): boolean {
  return /^policy_tree\.[a-z0-9_.]+$/i.test(label.trim());
}

export function policyVersionDisplayName(v: ReportStrategyPolicyVersion): string {
  const title = typeof v.title === "string" ? v.title.trim() : "";
  if (title) return title;
  if (typeof v.version_number === "number" && Number.isFinite(v.version_number)) {
    return `v${v.version_number}`;
  }
  return "policy version";
}

/** Human-readable policy name for active composed Pinescript provenance. */
export function resolveActiveComposedPolicyName(
  active?: StrategyActivePineSummary | null,
  versions?: ReportStrategyPolicyVersion[]
): string | null {
  if (!active) return null;

  const ids = Array.isArray(active.last_compose_policy_version_ids)
    ? active.last_compose_policy_version_ids.filter(
        (id): id is string => typeof id === "string" && id.length > 0
      )
    : [];

  if (versions?.length && ids.length) {
    const matched = ids
      .map((id) => versions.find((v) => v.id === id))
      .filter((v): v is ReportStrategyPolicyVersion => Boolean(v));
    if (matched.length === 1) {
      return policyVersionDisplayName(matched[0]);
    }
    if (matched.length > 1) {
      return matched.map(policyVersionDisplayName).join(", ");
    }
  }

  const backendLabel = active.last_compose_policy_label?.trim();
  if (backendLabel && !isPolicySchemaVersionLabel(backendLabel)) {
    return backendLabel;
  }

  if (ids.length === 1) return "saved policy version";
  if (ids.length > 1) return `${ids.length} saved policy versions`;
  return null;
}

const COMPOSE_MODE_STORAGE_PREFIX = "strategy_compose_mode_v1:";
const TV_EDUCATION_ACK_PREFIX = "strategy_pine_tv_education_ack_v1:";

export function loadStoredComposeMode(strategyId: string): ComposeOutputMode | null {
  try {
    const raw = window.localStorage.getItem(`${COMPOSE_MODE_STORAGE_PREFIX}${strategyId}`);
    if (
      raw === "merge_into_strategy" ||
      raw === "separate_indicator" ||
      raw === "append_policy_to_indicator"
    ) {
      return raw;
    }
  } catch {
    // no-op
  }
  return null;
}

export function storeComposeMode(strategyId: string, mode: ComposeOutputMode): void {
  try {
    window.localStorage.setItem(`${COMPOSE_MODE_STORAGE_PREFIX}${strategyId}`, mode);
  } catch {
    // no-op
  }
}

export function hasAcknowledgedTvEducation(strategyId: string): boolean {
  try {
    return window.localStorage.getItem(`${TV_EDUCATION_ACK_PREFIX}${strategyId}`) === "1";
  } catch {
    return false;
  }
}

export function acknowledgeTvEducation(strategyId: string): void {
  try {
    window.localStorage.setItem(`${TV_EDUCATION_ACK_PREFIX}${strategyId}`, "1");
  } catch {
    // no-op
  }
}

export function pickDefaultComposeMode(
  strategyId: string,
  fields: ReportStrategyPineFields | null | undefined,
  parsed?: ReportStrategyPineParseResult | null
): ComposeOutputMode {
  if (!fields) return "merge_into_strategy";
  const treeDefault = resolveDefaultComposeMode(strategyId, fields, parsed);
  if (isIndicatorPineScript(fields, parsed)) {
    const stored = loadStoredComposeMode(strategyId);
    if (stored === "separate_indicator" || stored === "append_policy_to_indicator") {
      return stored;
    }
    return treeDefault;
  }
  const stored = loadStoredComposeMode(strategyId);
  if (stored && stored !== "append_policy_to_indicator") return stored;
  const inject = getEffectiveInjectReadiness(fields, parsed) ?? fields.pine_parse_summary?.inject_readiness;
  if (inject === "ready" || inject === "needs_review") {
    const fromFields = fields.last_compose_mode || fields.default_compose_mode;
    if (fromFields && fromFields !== "append_policy_to_indicator") return fromFields;
    return treeDefault;
  }
  return "separate_indicator";
}

const COMPOSE_MODE_LABELS: Record<string, string> = {
  merge_into_strategy: "Merge into strategy",
  append_policy_to_indicator: "Append policy to indicator",
  separate_indicator: "Separate indicator",
};

export function formatComposeModeLabel(mode?: string | null): string {
  if (!mode) return "Compose";
  return COMPOSE_MODE_LABELS[mode] ?? mode.replace(/_/g, " ");
}

export type ActivePineProvenanceParts = {
  composeMode: ComposeOutputMode;
  composeModeLabel: string;
  composeModeDescription: string;
  tail: string;
};

function activePineProvenanceTail(active: StrategyActivePineSummary): string {
  const tailParts: string[] = [];
  const backendLabel = active.last_compose_policy_label?.trim();
  if (backendLabel && !isPolicySchemaVersionLabel(backendLabel)) {
    tailParts.push(backendLabel);
  } else if (active.last_compose_policy_version_ids?.length) {
    tailParts.push(`${active.last_compose_policy_version_ids.length} policy version(s)`);
  }
  if (active.last_composed_at) {
    const d = new Date(active.last_composed_at);
    if (!Number.isNaN(d.getTime())) {
      tailParts.push(d.toISOString().replace("T", " ").slice(0, 19) + " UTC");
    }
  }
  return tailParts.join(" · ");
}

export function activePineProvenanceParts(
  active?: StrategyActivePineSummary | null
): ActivePineProvenanceParts | null {
  if (!active?.last_compose_mode) return null;
  const help = getComposeModeHelpEntry(active.last_compose_mode);
  return {
    composeMode: active.last_compose_mode,
    composeModeLabel: help?.label ?? formatComposeModeLabel(active.last_compose_mode),
    composeModeDescription: help?.description ?? "",
    tail: activePineProvenanceTail(active),
  };
}

export function formatActivePineProvenance(active?: StrategyActivePineSummary | null): string {
  const parts = activePineProvenanceParts(active);
  if (parts) {
    const bits = [`Compose Mode: ${parts.composeModeLabel}`];
    if (parts.tail) bits.push(parts.tail);
    return bits.join(" · ");
  }
  if (!active) return "";
  const tail = activePineProvenanceTail(active);
  return tail;
}

/** Native `title` on badges — provenance line plus compose mode description. */
export function formatActivePineProvenanceBadgeTitle(
  active?: StrategyActivePineSummary | null
): string | undefined {
  const text = formatActivePineProvenance(active);
  if (!text) return undefined;
  const parts = activePineProvenanceParts(active);
  if (parts?.composeModeDescription) {
    return `${text}. ${parts.composeModeDescription}`;
  }
  return text;
}

export const PINE_SCRIPT_CREATED_LABEL = "Created";
export const PINE_SCRIPT_NOT_CREATED_LABEL = "Not created";
export const PINE_SCRIPT_UPLOADED_LABEL = "Uploaded";
export const PINE_SCRIPT_NOT_UPLOADED_LABEL = "Not uploaded";

/** Master script on detail (pine_source / pinescript) or list metadata after parse (body omitted on list). */
export function strategyHasMasterPinescriptUploaded(
  strategy: ReportStrategyPineFields | null | undefined
): boolean {
  if (!strategy) return false;
  if (Boolean(extractPineScriptText(strategy).trim())) return true;
  const merged = mergeStrategyWithParseSummary(strategy as ReportStrategyResponse);
  const status = merged.pine_parse_status;
  return status === "full" || status === "partial" || status === "failed";
}

/** @deprecated Use strategyHasMasterPinescriptUploaded */
export const strategyHasMasterPinescriptCreated = strategyHasMasterPinescriptUploaded;

/** True when a composed TradingView export exists (including outdated / stale). */
export function strategyHasTradingViewPinescriptCreated(
  strategy: ReportStrategyPineFields | null | undefined
): boolean {
  if (!strategy) return false;
  const status = strategy.active_pine?.status;
  return Boolean(
    strategy.has_composed_pine || status === "composed" || status === "stale"
  );
}

export function masterPineUploadStatusLabel(
  strategy: ReportStrategyPineFields | null | undefined
): string {
  return strategyHasMasterPinescriptUploaded(strategy)
    ? PINE_SCRIPT_UPLOADED_LABEL
    : PINE_SCRIPT_NOT_UPLOADED_LABEL;
}

/** @deprecated Use masterPineUploadStatusLabel */
export const masterPineCreatedStatusLabel = masterPineUploadStatusLabel;

export function tradingViewPineCreatedStatusLabel(
  strategy: ReportStrategyPineFields | null | undefined
): string {
  return strategyHasTradingViewPinescriptCreated(strategy)
    ? PINE_SCRIPT_CREATED_LABEL
    : PINE_SCRIPT_NOT_CREATED_LABEL;
}

export function activePineStatusLabel(active?: StrategyActivePineSummary | null): string | null {
  if (!active?.status) return null;
  switch (active.status) {
    case "composed":
      return PINE_SCRIPT_CREATED_LABEL;
    case "stale":
      return "Outdated";
    case "none":
      return PINE_SCRIPT_NOT_CREATED_LABEL;
    default:
      return null;
  }
}

export function isSeparateIndicatorComposeMode(
  mode: string | null | undefined
): boolean {
  return mode === "separate_indicator";
}

/** Last successful compose used separate-indicator mode (two TV scripts). */
export function activeComposeUsesSeparateIndicator(
  fields: ReportStrategyPineFields | null | undefined
): boolean {
  return isSeparateIndicatorComposeMode(fields?.active_pine?.last_compose_mode);
}

/** Indicator overlay download/copy — only valid for separate_indicator compose. */
export function shouldShowIndicatorOverlayArtifact(
  fields: ReportStrategyPineFields | null | undefined
): boolean {
  return activeComposeUsesSeparateIndicator(fields) && Boolean(fields?.has_indicator_pine);
}

export function activeComposedScriptTitle(
  outputMode: string | null | undefined,
  isSeparateIndicator: boolean
): string {
  if (isSeparateIndicator) return "Your strategy (snapshot at compose)";
  if (outputMode === "merge_into_strategy") return "Merged strategy script";
  if (outputMode === "append_policy_to_indicator") return "Indicator with policy";
  return formatComposeModeLabel(outputMode);
}

export function activeComposedScriptDescription(
  outputMode: string | null | undefined,
  role: "composed" | "indicator_overlay"
): string {
  if (role === "indicator_overlay") {
    return "Policy as a separate TradingView indicator. Add it on the same chart for on-chart labels and blocked-trade context.";
  }
  if (isSeparateIndicatorComposeMode(outputMode)) {
    return "Your master strategy unchanged at compose time. Paste into a strategy study on TradingView.";
  }
  if (outputMode === "merge_into_strategy") {
    return "One strategy script with Tessera policy gates merged into your trade entries. Paste into a strategy study on TradingView.";
  }
  if (outputMode === "append_policy_to_indicator") {
    return "Your indicator plots stay as written; Tessera appends a policy block at the end. Paste into one indicator study.";
  }
  return "Generated Pinescript from your last compose. Copy or download for TradingView.";
}

export function canDownloadActiveComposedPine(strategy: ReportStrategyResponse): boolean {
  const ap = strategy.active_pine;
  if (ap?.compose_stale || ap?.status === "stale") return false;
  return Boolean(strategy.has_composed_pine);
}

export function filterPolicyVersionsForCompose(
  versions: ReportStrategyPolicyVersion[],
  includeArchived: boolean
): ReportStrategyPolicyVersion[] {
  if (includeArchived) return versions;
  return versions.filter((v) => !v.is_archived);
}

export function extractPineScriptText(resp: unknown): string {
  if (!resp || typeof resp !== "object") return "";
  const row = resp as { pinescript?: unknown; pine_source?: unknown };
  if (typeof row.pinescript === "string") return row.pinescript;
  if (typeof row.pine_source === "string") return row.pine_source;
  return "";
}

export function versionOptionLabel(v: ReportStrategyPolicyVersion): string {
  const num =
    typeof v.version_number === "number" && Number.isFinite(v.version_number)
      ? `v${v.version_number}`
      : "version";
  const title = typeof v.title === "string" && v.title.trim() ? ` · ${v.title.trim()}` : "";
  const bundle = isPolicyBundleVersion(v) ? " [bundle]" : "";
  const current = v.is_current ? " (current)" : "";
  return `${num}${title}${bundle}${current}`;
}
